import _ from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { connect, ConnectedProps } from 'react-redux';
import produce from 'immer';
import cn from 'classnames';
import { BBCommonProps, CardData, StoryModel } from 'types/story';
import { CARD_TYPE, COMPONENT_STATES, COMPONENT_TYPE, HAS_ANSWERS_ATTR } from 'common/constants';
import { sleep } from 'common/utils';
import { ClientReducerState } from 'client/reducers';
import { AnswersStorage, isPreview } from 'client/utils';
import { sendAnswer, getCardVotes } from 'client/actions/api';
import { updateAnswers } from 'client/actions/update-answers';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { ScriptRenderer } from 'client/components/common/ScriptRenderer/ScriptRenderer';
import { EventProviderContextT, withEventProvider } from 'client/components/pages/Story/EventProvider/Context';
import css from './Answer.scss';

const PREVIEW_VOTES = {};
const WRONG_ANSWER_DELAY = 500;
const extraAttrs = { [HAS_ANSWERS_ATTR]: false };

function getResult(id: Props['_id'], answersSettings: Props['answersSettings']) {
	const isCorrect = answersSettings?.[id]?.isCorrect ?? false;
	return {
		correct: isCorrect,
		incorrect: !isCorrect,
	};
}

function setPreviewVotes(answerId: string, cardId: string) {
	if (PREVIEW_VOTES[cardId][answerId] !== -1) {
		return;
	}

	const votesAll = Object.values(PREVIEW_VOTES[cardId]);
	const votes = votesAll.filter(v => v !== -1);
	const isLast = votesAll.length - votes.length === 1;
	const total = _.sum(votes);
	const rest = 100 - total;

	PREVIEW_VOTES[cardId][answerId] = isLast ? rest : _.random(0, rest);
}

const memo = {};

const connector = connect(
	(state: ClientReducerState, ownProps: BBCommonProps) => {
		if (!memo[ownProps._id]) {
			// why? - to avoid update component with next card from state
			memo[ownProps._id] = state.card;
		}

		const { user } = state;
		const story = state.story as StoryModel;
		const card = (memo[ownProps._id] || state.card) as CardData;
		const showResults = card.settings?.showResults;
		const cardId = card._id;
		const settings = story.storyVersions.latest.settings.cards?.[cardId];
		const isCanBeCorrect =
			(card.type === CARD_TYPE.TRIVIA && settings?.showCorrect) || card.type === CARD_TYPE.TRUE_OR_FALSE;

		return {
			storyId: story.id,
			cardId,
			showResults,
			isCanBeCorrect,
			answersSettings: settings?.answers,
			showCorrect: isCanBeCorrect ? settings?.showCorrect ?? true : false,
			answersData: produce(user.answers[cardId], draft => {
				// Provide fake votes result to props
				if (isPreview && showResults) {
					if (_.isUndefined(_.get(PREVIEW_VOTES, [cardId, ownProps._id]))) {
						_.set(PREVIEW_VOTES, [cardId, ownProps._id], -1);
					}

					if (draft && draft.selected.length > 0) {
						setPreviewVotes(ownProps._id, cardId);

						_.set(draft, 'votes', PREVIEW_VOTES[cardId]);
					}
				}
			}),

			// NOTE: multiple answers is not implemented yet, added just for a future purposes
			answersMax: settings?.answersMax ?? 1,
		};
	},
	{
		sendAnswer,
		updateAnswers,
		getCardVotes,
	}
);

type Props = ConnectedProps<typeof connector> & BBCommonProps & EventProviderContextT;

type State = { result: null | { correct: boolean; incorrect: boolean } };

/**
 * How does it work:
 * 1. Click on Answer
 * 2. Send answer (only prod)
 * 3. Get votes statistics from BE (only prod & showResults & hasResultBlocks)
 * 4. Store answer & votes in client redux store
 * 4.1. re-render component and provide answerData
 * 4.2. @:getDerivedStateFromProps: add answer result to state of selected Answer
 * 4.2.1. @:componentDidUpdate: (only if "isCanBeCorrect": true) update correct|incorrect state and show it visually
 * 4.3. (only if "showResults" & "hasResultBlocks") render children with votes results from 4.1 (ResultText|ResultShape)
 */
class Answer extends React.Component<Props, State> {
	state = {
		result: null,
	};

	/**
	 * User can delete result blocks in Answer and in this case showResults must a false.
	 */
	hasResultBlocks?: boolean = false;

	answerRef: React.RefObject<HTMLDivElement> = React.createRef();

	static getDerivedStateFromProps(props: Props, state: State): Partial<State> {
		const nextState: Partial<State> = {};

		const isCurrentAnswered = props.answersData?.selected?.includes(props._id);
		if (isCurrentAnswered) {
			nextState.result = getResult(props._id, props.answersSettings);
		}

		return nextState;
	}

	componentDidMount() {
		if (!this.props.isEditableMode) {
			// Register component events in provider, potentially Swipe BB can use it.
			this.props.eventProvider.setComponentEvents(this.props._id, { onClick: this.nonEditorListeners.onClick! });
		}
	}

	componentDidUpdate(prevProps: Props, prevState: State) {
		if (this.props.isCanBeCorrect) {
			this.showAnswerResultByCorrectProp();
		}
	}

	get selected() {
		return this.props.answersData?.selected ?? [];
	}

	get isAllowAnswer() {
		let isAllow = this.selected.length === 0;

		if (this.props.answersMax > 1) {
			isAllow = this.selected.length < this.props.answersMax;
		}

		return isAllow;
	}

	get isCorrect() {
		return this.props.answersSettings?.[this.props._id]?.isCorrect;
	}

	// return timeout in milliseconds
	get answerTimeout() {
		const min = this.showResults ? 3 : 0;
		return Math.max(min, +(this.props.uiConfig.componentProps.answerTimeout || min)) * 1000;
	}

	get nonEditorListeners() {
		if (this.props.isEditableMode) {
			return {};
		}

		return {
			/**
			 * @param event is optional, because may be invoked programmatically by another component
			 *        Event can be equal to "swipe" only when it is called by Swipe BB
			 */
			onClick: async (event?: React.SyntheticEvent | 'swipe') => {
				if (!this.isAllowAnswer) {
					return;
				}

				const nextStates = { [COMPONENT_STATES.HOVER]: false };

				if (!this.props.isCanBeCorrect) nextStates[COMPONENT_STATES.SELECTED] = true;

				this.props.setStates(nextStates);

				// Call event assigned by Swipe BB (optional. example is a "TrueOrFalse|ThisOrThat" card)
				const providedSwipe = this.props.eventProvider.events.component[this.props._id]?.onSwipe;
				if (event !== 'swipe' && providedSwipe) {
					providedSwipe();
					return;
				}

				const { showResults } = this;
				const { cardId, storyId, _id } = this.props;

				const sendAnswerPayload = {
					storyId,
					cardId,
					answerId: _id,
					answerText: this.answerRef.current?.textContent || 'MediaContent*',
				};

				let votes: { [key: string]: number } = {};
				if (showResults && !isPreview) {
					// Store answer in BE
					await this.props.sendAnswer(sendAnswerPayload);
					// Get votes distribution
					votes = await this.props.getCardVotes({ cardId, storyId });
				} else if (!isPreview) {
					// Store answer in BE
					this.props.sendAnswer(sendAnswerPayload);
				}

				// Store answer in redux
				this.props.updateAnswers({
					cardId: this.props.cardId,
					answerId: _id,
					votes,
				});

				// Store answer to localStorage (used then to calculated a total score)
				new AnswersStorage({ storyId, cardId }).add(_id);

				// Render answer embed code
				const script = document.createElement('div');
				script.id = `answer-script-${cardId}-${_id}`;
				document.body.appendChild(script);
				ReactDOM.render(
					<ScriptRenderer value={this.props.uiConfig.componentProps?.other?.embedCode ?? ''} scriptOnly />,
					script
				);

				if (this.props.showCorrect && !this.isCorrect) {
					// wait WRONG_ANSWER_DELAY to show correct and wait 500 before to go forward
					await sleep(WRONG_ANSWER_DELAY + 500);
				} else if (nextStates[COMPONENT_STATES.SELECTED]) {
					await sleep(500);
				}

				await sleep(this.answerTimeout);
				this.props.eventProvider.events.flow(_id);
			},
		};
	}

	get showResults() {
		return this.props.showResults && this.hasResultBlocks;
	}

	/**
	 * Update answer components states, to show visually is answer correct or not.
	 */
	showAnswerResultByCorrectProp = async () => {
		const p: Props = this.props;
		const s: State = this.state;
		const { CORRECT, INCORRECT } = COMPONENT_STATES;

		/**
		 * Update correct|incorrect states at "this.props.states" & "this.props.statesAttrs"
		 */
		function updateCorrectStates() {
			if (!s.result) {
				return;
			}

			const { correct, incorrect } = s.result;

			if (correct !== p.states[CORRECT] || incorrect !== p.states[INCORRECT]) {
				p.setStates({
					...p.states,
					[CORRECT]: correct,
					[INCORRECT]: incorrect,
				});
			}
		}

		updateCorrectStates();

		const forceShowCorrect = p.showCorrect && !s.result && this.selected.length === p.answersMax && this.isCorrect;
		if (forceShowCorrect) {
			await sleep(WRONG_ANSWER_DELAY);
			this.setState({ result: getResult(p._id, p.answersSettings) });
		}
	};

	mapChildren = () =>
		React.Children.map(this.props.children, child => {
			if (!React.isValidElement<BBCommonProps>(child)) {
				return child;
			}

			const nextProps: Partial<BBCommonProps> = {
				...child.props,
				parentStates: this.props.states,
			};

			if (child.props.type === COMPONENT_TYPE.RESULT_TEXT || child.props.type === COMPONENT_TYPE.RESULT_SHAPE) {
				if (this.props.showResults) {
					this.hasResultBlocks = true;
				}

				const isCorrect = this.props.states[COMPONENT_STATES.CORRECT];
				const isShow = !this.props.isEditableMode && this.selected.length === this.props.answersMax;
				const noVotes = _.isEmpty(this.props.answersData?.votes);
				let value = this.props.answersData?.votes?.[this.props._id];

				if (value === undefined) {
					value = isCorrect && noVotes ? 100 : 0;
				}

				// Provide vote data to child components to display vote results (see ResultText, ResultShape)
				nextProps.answerVote = {
					isShow,
					value,
					duration: this.answerTimeout * 0.35,
				};
			}

			return React.cloneElement<BBCommonProps>(child, nextProps);
		});

	renderEditable() {
		const props = { ...this.props.editableModeProps?.nodeProps };
		return this.renderDefault(props);
	}

	renderDefault(props: any = {}) {
		const { nodeProps } = this.props.uiConfig;

		extraAttrs[HAS_ANSWERS_ATTR] = !!this.selected.length;

		return (
			<div
				{...this.props.uiConfig.nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				{...this.nonEditorListeners}
				{...props}
				{...extraAttrs}
				ref={this.answerRef}
				className={cn(css.answer, nodeProps.className, props.className)}
			>
				{this.props.isEditableMode ? this.props.children : this.mapChildren()}
			</div>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

const WithRedux = connector(Answer);
const WithCardTransitionContext = withCardTransitionContext(WithRedux);

export default withEventProvider(WithCardTransitionContext);
